import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { isNativePlatform } from "../lib/platform";
import type { User } from "@supabase/supabase-js";

const NATIVE_REDIRECT_URL = "com.nigunim.app://auth/callback";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileName, setProfileName] = useState<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  const fetchProfileName = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("display_name")
      .eq("id", userId)
      .single();
    setProfileName(data?.display_name ?? null);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const u = data.session?.user ?? null;
      userIdRef.current = u?.id ?? null;
      setUser(u);
      if (u) fetchProfileName(u.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      const newId = u?.id ?? null;
      if (newId !== userIdRef.current) {
        userIdRef.current = newId;
        setUser(u);
        if (u) fetchProfileName(u.id);
        else setProfileName(null);
      }
    });

    let urlListenerCleanup: (() => void) | null = null;
    let cancelled = false;
    if (isNativePlatform()) {
      // Catch the deep-link callback from Google → Supabase → app, resolve the session,
      // and dismiss the in-app browser. Without this, sign-in completes in SFSafariViewController
      // and the native app stays signed out.
      (async () => {
        const { App } = await import("@capacitor/app");
        const { Browser } = await import("@capacitor/browser");
        if (cancelled) return;
        const handle = await App.addListener("appUrlOpen", async ({ url }) => {
          if (!url.startsWith(NATIVE_REDIRECT_URL)) return;
          try {
            const qIndex = url.indexOf("?");
            const hIndex = url.indexOf("#");
            const query = qIndex >= 0 ? new URLSearchParams(url.slice(qIndex + 1, hIndex >= 0 ? hIndex : undefined)) : null;
            const hash = hIndex >= 0 ? new URLSearchParams(url.slice(hIndex + 1)) : null;
            const code = query?.get("code");
            const accessToken = hash?.get("access_token");
            const refreshToken = hash?.get("refresh_token");
            if (code) {
              await supabase.auth.exchangeCodeForSession(code);
            } else if (accessToken && refreshToken) {
              await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
            } else {
              console.error("[auth] deep-link has no code or tokens", url);
            }
          } catch (err) {
            console.error("[auth] session resolution failed", err);
          } finally {
            await Browser.close();
          }
        });
        urlListenerCleanup = () => handle.remove();
      })();
    }

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      urlListenerCleanup?.();
    };
  }, [fetchProfileName]);

  const signInWithGoogle = async () => {
    if (isNativePlatform()) {
      const { Browser } = await import("@capacitor/browser");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: NATIVE_REDIRECT_URL, skipBrowserRedirect: true },
      });
      if (error || !data?.url) throw error ?? new Error("No OAuth URL");
      await Browser.open({ url: data.url, presentationStyle: "popover" });
      return;
    }
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  };

  const signInWithApple = async () => {
    if (isNativePlatform()) {
      const { Browser } = await import("@capacitor/browser");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: { redirectTo: NATIVE_REDIRECT_URL, skipBrowserRedirect: true },
      });
      if (error || !data?.url) throw error ?? new Error("No OAuth URL");
      await Browser.open({ url: data.url, presentationStyle: "popover" });
      return;
    }
    await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: window.location.origin },
    });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    userIdRef.current = null;
    setUser(null);
    setProfileName(null);
  };

  const updateDisplayName = useCallback(async (name: string) => {
    if (!user) return;
    const trimmed = name.trim().slice(0, 30);
    if (!trimmed) return;
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: trimmed, updated_at: new Date().toISOString() })
      .eq("id", user.id);
    if (!error) setProfileName(trimmed);
  }, [user]);

  const displayName = profileName
    ?? user?.user_metadata?.full_name
    ?? user?.user_metadata?.name
    ?? user?.email
    ?? null;
  const avatarUrl = user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null;

  return { user, loading, displayName, avatarUrl, signInWithGoogle, signInWithApple, signOut, updateDisplayName };
}
